import { getSectionDisplayNames } from '@tunasong/models'
import { ChordLib, isChordVariant } from '@tunasong/music-lib'
import type { CommentAnchor } from '@tunasong/schemas'

/** A block in the open song that has a comment thread, as the sidebar Chat names it (chat makeover M3, #2718) */
export interface CommentedBlock {
  id: string
  /** What follows "On " in the group header: a lyric, a section or chord name, or C7's fallback */
  label: string
  /** A lyric is quoted; names and the fallback are not */
  quoted: boolean
  /** C7's fallback ("this line", "this block"): it names no particular block */
  generic?: true
  /** The type of the section the block sits in, for the accent colour; none outside a section */
  sectionType?: string
}

interface DocNode {
  type?: string
  id?: string
  comments?: boolean
  sectionType?: string
  text?: string
  children?: DocNode[]
}

/** Long enough for the visible ellipsis to use a desktop's width; short enough not to read out a paragraph (Joni) */
const maxLabelLength = 80

const cap = (label: string) =>
  label.length > maxLabelLength ? `${label.slice(0, maxLabelLength - 1).trimEnd()}…` : label

const isElement = (node: DocNode) => Array.isArray(node.children)

const chordName = (node: DocNode, transpose: number) =>
  isChordVariant(node) ? ChordLib.getName(ChordLib.transpose(node, transpose)) : undefined

/** The text under a node, leaving out chord chips, whose text would otherwise read "C Walking F down" */
const lyricText = (node: DocNode): string => {
  if (!isElement(node)) {
    return node.text ?? ''
  }
  if (node.type === 'chord') {
    return ''
  }
  return (node.children ?? []).map(lyricText).join('')
}

const chordsIn = (node: DocNode): DocNode[] => {
  if (node.type === 'chord') {
    return [node]
  }
  return (node.children ?? []).flatMap(chordsIn)
}

const lineLabel = (node: DocNode, transpose: number) => {
  const lyric = lyricText(node).replace(/\s+/g, ' ').trim()
  if (lyric) {
    return { label: cap(lyric), quoted: true }
  }
  const chords = chordsIn(node)
    .map(chord => chordName(chord, transpose))
    .filter((name): name is string => Boolean(name))
  if (chords.length > 0) {
    return { label: cap(chords.join(' ')), quoted: true }
  }
  return { label: 'this line', quoted: false, generic: true as const }
}

const sectionsIn = (nodes: DocNode[]): DocNode[] =>
  nodes.flatMap(node => [...(node.type === 'section' ? [node] : []), ...sectionsIn(node.children ?? [])])

/**
 * The commented blocks of a document, in document order, labelled per Joni's answer (2026-10-24): a line by its
 * lyric (or its chords when it has no lyric), a section by the display name its heading shows, a chord by its
 * transposed name, and C7 ("this line", "this block") otherwise.
 */
export const collectCommentedBlocks = (
  children: unknown[],
  { transpose = 0 }: { transpose?: number } = {}
): CommentedBlock[] => {
  const nodes = children as DocNode[]
  const sections = sectionsIn(nodes)
  const sectionNames = new Map(
    getSectionDisplayNames(sections.map(node => node.sectionType)).map((name, index) => [sections[index], name])
  )

  const labelFor = (node: DocNode) => {
    if (node.type === 'section') {
      const name = sectionNames.get(node) ?? node.sectionType
      return name ? { label: name, quoted: false } : { label: 'this section', quoted: false, generic: true as const }
    }
    if (node.type === 'chord') {
      const name = chordName(node, transpose)
      return name ? { label: name, quoted: false } : { label: 'this chord', quoted: false, generic: true as const }
    }
    if (node.type === 'line') {
      return lineLabel(node, transpose)
    }
    return { label: 'this block', quoted: false, generic: true as const }
  }

  const walk = (list: DocNode[], sectionType: string | undefined): CommentedBlock[] =>
    list.flatMap(node => {
      if (!isElement(node)) {
        return []
      }
      const inside = node.type === 'section' ? node.sectionType : sectionType
      const own = node.comments && node.id ? [{ id: node.id, ...labelFor(node), sectionType: inside }] : []
      return [...own, ...walk(node.children ?? [], inside)]
    })

  return walk(nodes, undefined)
}

/** The group header: "On “Walking down”", "On Verse 2", "On this line" */
export const commentedBlockHeading = ({ label, quoted }: Pick<CommentedBlock, 'label' | 'quoted'>) =>
  quoted ? `On “${label}”` : `On ${label}`

/**
 * Where a comment on this block is written (GH #2795): the document and the block, with the group header's label as
 * charted, a lyric in quotes. A generic fallback names no block, so it is left out; the notification then names the
 * document only.
 */
export const commentAnchorFor = ({
  nodes,
  entityId,
  elementId,
}: {
  nodes: unknown[]
  entityId?: string
  elementId?: string
}): CommentAnchor | undefined => {
  if (!(entityId && elementId)) {
    return undefined
  }
  const block = collectCommentedBlocks(nodes).find(candidate => candidate.id === elementId)
  const label = block && !block.generic ? (block.quoted ? `“${block.label}”` : block.label) : undefined
  return { entityId, elementId, ...(label ? { label } : {}) }
}
